import React from "react";

// Source images for the educational sections — sourced from user uploads.
// Strategic placement matches each image's subject to its section.
const IMG_WHOLE_PERSON =
  "https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/59hgx61j_Mmay3yMtLRFNvd0cNHz5k.png";
// Nervous system illustrated with golden glowing nerves through the body —
// a much stronger visual for the vagus / calming system section than the
// earlier heart-lungs anatomical render.
const IMG_NERVOUS_SYSTEM =
  "https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/dq8agt3d_lmDF9CR0IeiyJ8eKAUB3F.png";
// Calm seated figure with visible organs — the "destination state" that
// bilateral rhythm guides the patient toward.
const IMG_SEATED_CALM =
  "https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/as2hot9q_3Vdc4KQLCxxZm-mKvprEe.png";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

/**
 * EducationalSections — the four science explainers, condensed into a
 * single accordion so the homepage stays a short, scannable scroll.
 */
export default function EducationalSections() {
  return (
    <section id="science" className="mt-24 fade-up" data-testid="educational-sections">
      <div className="iw-section-head">
        <div className="iw-section-head__label">§ 04 — The Science, Simply</div>
        <h2 className="iw-section-head__title">
          Why this works — open what you're curious about.
        </h2>
      </div>
      <Accordion
        type="single"
        collapsible
        className="iw-card px-5 sm:px-8"
        data-testid="science-accordion"
      >
        <AccordionItem value="domains" id="domains">
          <AccordionTrigger className="hover:no-underline" data-testid="science-domains-trigger">
            <TriggerLabel n="01" title="The 9 Domains of Wellness" />
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-2 pb-4"><DomainsSection /></div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="calming" id="calming">
          <AccordionTrigger className="hover:no-underline" data-testid="science-calming-trigger">
            <TriggerLabel n="02" title="Your Body's Calming System" />
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-2 pb-4"><VagusSection /></div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="rhythm" id="rhythm-education">
          <AccordionTrigger className="hover:no-underline" data-testid="science-rhythm-trigger">
            <TriggerLabel n="03" title="Why Left-Right Rhythm Settles You" />
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-2 pb-4"><BilateralSection /></div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="grounding" id="grounding" className="border-b-0">
          <AccordionTrigger className="hover:no-underline" data-testid="science-grounding-trigger">
            <TriggerLabel n="04" title="Grounding: How Each Session Ends" />
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-2 pb-4"><GroundingSection /></div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}

function TriggerLabel({ n, title }) {
  return (
    <span className="flex items-baseline gap-4 text-left">
      <span
        className="iw-mono uppercase flex-shrink-0"
        style={{ color: "var(--iw-accent-red)", fontSize: "11px", letterSpacing: "0.22em", fontWeight: 700 }}
      >
        {n}
      </span>
      <span className="serif text-lg sm:text-xl" style={{ color: "var(--iw-primary)", fontWeight: 700 }}>
        {title}
      </span>
    </span>
  );
}

const DOMAINS = [
  { n: 1, name: "Physical", copy: "How your body supports or limits your life." },
  { n: 2, name: "Emotional", copy: "Your inner experience and resilience." },
  { n: 3, name: "Social", copy: "Your relationships and community." },
  { n: 4, name: "Vocational", copy: "Your relationship with work and purpose." },
  { n: 5, name: "Financial", copy: "Your sense of stability and security." },
  { n: 6, name: "Spiritual", copy: "Connection to something larger." },
  { n: 7, name: "Environmental", copy: "Your physical surroundings." },
  { n: 8, name: "Intellectual", copy: "Curiosity, learning, and mental engagement." },
  {
    n: 9,
    name: "Autonomic / Recovery",
    copy:
      "Your nervous system's ability to calm, recover, and build resilience. This is the home domain of the Self-Regulation Console.",
    home: true,
  },
];

function DomainsSection() {
  return (
    <section data-testid="domains-section">
      <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 md:gap-10 items-center mb-10">
        <SectionHeader
          eyebrow="The 9 Domains of Wellness"
          title="Whole-person wellness, one domain at a time."
          body="Integrated Wellness looks at the whole person across nine connected domains. This tool lives in the ninth — your nervous system's ability to recover — but a calmer nervous system supports every other area of your life."
          noBottomMargin
          className="order-1"
        />
        <figure
          className="relative overflow-hidden order-2 max-w-md mx-auto md:max-w-none w-full"
          style={{
            aspectRatio: "1 / 1",
            background: "linear-gradient(160deg, #11296b 0%, #1e3a8a 100%)",
            boxShadow: "0 28px 60px -30px rgba(17,41,107,0.40)",
            borderRadius: "2px",
            border: "1px solid var(--iw-border)",
          }}
        >
          <img
            src={IMG_WHOLE_PERSON}
            alt="Whole-person wellness — energy fields woven around the body"
            className="block w-full h-full object-cover"
            loading="lazy"
            data-testid="domains-hero-image"
          />
          <figcaption
            className="absolute bottom-3 left-4 font-mono text-[10px] tracking-[0.24em] uppercase"
            style={{ color: "rgba(255,255,255,0.78)" }}
          >
            Whole-person · interconnected
          </figcaption>
        </figure>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {DOMAINS.map((d) => (
          <DomainCard key={`domain-${d.n}`} d={d} />
        ))}
      </div>
    </section>
  );
}

function DomainCard({ d }) {
  const isHome = !!d.home;
  return (
    <div
      data-testid={`domain-card-${d.n}`}
      className="relative p-5 sm:p-6 transition-all"
      style={{
        background: isHome ? "linear-gradient(180deg, #FEF2F2 0%, #FFF 100%)" : "#fff",
        border: `1px solid ${isHome ? "var(--iw-accent-red)" : "var(--iw-border)"}`,
        borderRadius: "2px",
        boxShadow: isHome
          ? "0 18px 36px -20px rgba(217,4,41,0.22)"
          : "0 1px 0 rgba(17,41,107,0.04)",
      }}
    >
      {isHome && (
        <div
          className="absolute -top-3 left-5 px-2.5 py-1 iw-mono uppercase"
          style={{
            background: "var(--iw-accent-red)",
            color: "#fff",
            borderRadius: "2px",
            boxShadow: "0 6px 18px -8px rgba(217,4,41,0.40)",
            fontSize: "10px",
            letterSpacing: "0.20em",
            fontWeight: 600,
          }}
        >
          Home Domain
        </div>
      )}
      <div
        className="iw-mono uppercase mb-2"
        style={{
          color: isHome ? "var(--iw-accent-red)" : "var(--iw-muted)",
          letterSpacing: "0.20em",
          fontSize: "11px",
          fontWeight: 600,
        }}
      >
        Domain · {String(d.n).padStart(2, "0")}
      </div>
      <div
        className="serif text-lg sm:text-xl leading-tight mb-2"
        style={{ color: "var(--iw-primary)", fontWeight: 700 }}
      >
        {d.name}
      </div>
      <p className="text-[14px] leading-relaxed" style={{ color: "var(--iw-body)" }}>
        {d.copy}
      </p>
    </div>
  );
}

function VagusSection() {
  return (
    <section data-testid="vagus-section">
      <div className="grid md:grid-cols-[1.2fr_1fr] gap-6 md:gap-10 items-center">
        <div className="order-1 md:order-1">
          <SectionHeader
            eyebrow="Your Body's Calming System"
            title="Strengthening your built-in calm."
            body="Your vagus nerve is the main pathway of your body's 'rest and recover' branch. When it's active, your heart rate settles, your breathing slows, and your body shifts out of stress mode. Strengthening this 'vagal tone' over time helps you return to calm faster after stress — the foundation of the Autonomic / Recovery domain."
          />
          <p className="text-mute leading-relaxed text-base max-w-3xl mb-5">
            Simple practices support it, and several are built into your session:
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <PracticeCard
              title="Slow breathing"
              copy="Inhale through the nose, exhale longer than you inhale. The breathing pacer guides this for you."
            />
            <PracticeCard
              title="Humming or gentle vocal tone"
              copy="Soft sound vibrates the throat — a direct nudge to the calming branch of your nervous system."
            />
            <PracticeCard
              title="Tongue on the roof of your mouth"
              copy="A small, quiet anchor. Easy to do any time, anywhere, with no setup."
            />
            <PracticeCard
              title="Butterfly hug"
              copy="Cross your arms over your chest and gently alternate-tap your shoulders. Soothing, contained, self-administered."
            />
          </div>
        </div>
        <figure
          className="relative overflow-hidden order-2 md:order-2 max-w-md mx-auto md:max-w-none w-full"
          style={{
            aspectRatio: "1 / 1",
            background: "#F3F1ED",
            boxShadow: "0 28px 60px -30px rgba(17,41,107,0.18)",
            border: "1px solid var(--iw-border)",
            borderRadius: "2px",
          }}
        >
          <img
            src={IMG_NERVOUS_SYSTEM}
            alt="The body's nervous system, illustrated with the calming branch highlighted"
            className="block w-full h-full object-cover"
            loading="lazy"
            data-testid="vagus-hero-image"
          />
          <figcaption
            className="absolute bottom-3 left-4 font-mono text-[10px] tracking-[0.24em] uppercase"
            style={{ color: "rgba(15,26,46,0.55)" }}
          >
            Nervous system · head to toe
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

function BilateralSection() {
  return (
    <section data-testid="bilateral-section">
      <div className="grid md:grid-cols-[1fr_1.2fr] gap-6 md:gap-10 items-center mb-10">
        <figure
          className="relative overflow-hidden order-2 md:order-1 max-w-md mx-auto md:max-w-none w-full"
          style={{
            aspectRatio: "1 / 1",
            background: "#F3F1ED",
            boxShadow: "0 28px 60px -30px rgba(17,41,107,0.18)",
            border: "1px solid var(--iw-border)",
            borderRadius: "2px",
          }}
        >
          <img
            src={IMG_SEATED_CALM}
            alt="A calm seated figure — the settled state that bilateral rhythm helps you reach"
            className="block w-full h-full object-cover"
            loading="lazy"
            data-testid="bilateral-hero-image"
          />
          <figcaption
            className="absolute bottom-3 left-4 font-mono text-[10px] tracking-[0.24em] uppercase"
            style={{ color: "rgba(15,26,46,0.55)" }}
          >
            Settled · grounded · present
          </figcaption>
        </figure>
        <SectionHeader
          eyebrow="Why Left-Right Rhythm Settles You"
          title="A steady rhythm gives your attention something gentle to hold."
          body="Bilateral stimulation means a steady left-right alternating rhythm — through tapping, a moving visual, or sound. Engaging both sides of the body in a slow, predictable rhythm gives your attention a simple anchor and helps your nervous system downshift out of a wound-up state."
          noBottomMargin
          className="order-1 md:order-2"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <PracticeCard
          title="Mode A · Tap-Based"
          copy="You set the rhythm yourself by tapping left and right. Good for restless or wound-up moments where your hands want something to do."
          accent="var(--accent-blue, #153A79)"
        />
        <PracticeCard
          title="Mode B · Hands-Free"
          copy="You follow a smooth moving dot. Good for receptive, body-focused practice. Pair with breathing, humming, or the butterfly hug."
          accent="var(--accent-red, #BB1B1B)"
        />
      </div>
      <div
        className="p-5 sm:p-6 max-w-3xl"
        style={{
          background: "var(--iw-blue-tint)",
          border: "1px solid var(--iw-blue)",
          borderRadius: "2px",
        }}
        data-testid="emdr-distinction"
      >
        <div
          className="iw-mono uppercase mb-1.5"
          style={{
            color: "var(--iw-blue)",
            fontSize: "10px",
            letterSpacing: "0.22em",
            fontWeight: 600,
          }}
        >
          Important Distinction
        </div>
        <p className="text-[15px] leading-relaxed" style={{ color: "var(--iw-primary)" }}>
          This is a <strong>self-guided wellness and relaxation tool</strong>. It is{" "}
          <strong>not EMDR therapy</strong>, which is a clinical treatment delivered by
          licensed professionals.
        </p>
      </div>
    </section>
  );
}

function GroundingSection() {
  const steps = [
    { count: 5, sense: "things you can see" },
    { count: 4, sense: "things you can touch" },
    { count: 3, sense: "things you can hear" },
    { count: 2, sense: "things you can smell" },
    { count: 1, sense: "thing you can taste" },
  ];
  return (
    <section data-testid="grounding-section">
      <SectionHeader
        eyebrow="Grounding: How Each Session Ends"
        title="A gentle landing back into the present."
        body="Every session closes with a brief grounding ritual to bring you gently back to the present. The most common is the 5-4-3-2-1 method:"
      />
      <div className="grid grid-cols-5 sm:grid-cols-5 gap-2 sm:gap-3 mb-6">
        {steps.map((s) => (
          <div
            key={`ground-${s.count}-${s.sense}`}
            className="p-3 sm:p-5 text-center"
            style={{
              background: "#fff",
              border: "1px solid var(--iw-border)",
              borderRadius: "2px",
            }}
            data-testid={`ground-step-${s.count}`}
          >
            <div
              className="serif font-bold mb-1"
              style={{
                fontSize: "clamp(28px, 5vw, 44px)",
                color: "var(--iw-blue)",
                lineHeight: 1,
              }}
            >
              {s.count}
            </div>
            <div className="text-[11px] sm:text-[13px] leading-snug" style={{ color: "var(--iw-body)" }}>
              {s.sense}
            </div>
          </div>
        ))}
      </div>
      <p className="text-mute leading-relaxed text-base max-w-3xl">
        Grounding shifts your focus away from anxious thoughts and back to your senses,
        helping your body settle before you return to your day. It's simple, free, and
        something you can use any time — not just here.
      </p>
    </section>
  );
}

// ---------- small reusable bits ----------

function SectionHeader({ eyebrow, title, body, noBottomMargin = false, className = "" }) {
  return (
    <div className={`max-w-3xl ${noBottomMargin ? "" : "mb-7"} ${className}`}>
      <div
        className="iw-mono uppercase mb-3"
        style={{
          color: "var(--iw-accent-red)",
          fontSize: "11px",
          letterSpacing: "0.24em",
          fontWeight: 600,
        }}
      >
        {eyebrow}
      </div>
      <h2
        className="serif text-3xl sm:text-4xl leading-tight mb-4"
        style={{ fontWeight: 700, color: "var(--iw-primary)" }}
      >
        {title}
      </h2>
      <p className="leading-relaxed text-base sm:text-lg" style={{ color: "var(--iw-body)" }}>
        {body}
      </p>
    </div>
  );
}

function PracticeCard({ title, copy, accent }) {
  return (
    <div
      className="p-5"
      style={{
        background: "#fff",
        border: "1px solid var(--iw-border)",
        borderRadius: "2px",
      }}
    >
      <div
        className="serif text-lg mb-1.5"
        style={{ fontWeight: 700, color: accent || "var(--iw-primary)" }}
      >
        {title}
      </div>
      <p className="text-[14px] leading-relaxed" style={{ color: "var(--iw-body)" }}>
        {copy}
      </p>
    </div>
  );
}
